import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import rebrandIconSet from 'src/assets/icons/rebrandIconList.json'
import IcomoonReact from 'icomoon-react'
import elementStyles from 'src/components/Elements/elements.module.scss'

const Select = ({ id, value, options, onChange, placeholder, icon }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const selectRef = useRef(null)

  const toggleDropdown = () => setIsOpen((prev) => !prev)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleKeyDown = (event) => {
    if (!isOpen) return
    if (event.key === 'ArrowDown') {
      setHighlightedIndex((prev) => (prev + 1) % options.length)
    } else if (event.key === 'ArrowUp') {
      setHighlightedIndex((prev) => (prev - 1 + options.length) % options.length)
    } else if (event.key === 'Enter' && highlightedIndex >= 0) {
      onChange(options[highlightedIndex].value)
      setIsOpen(false)
    } else if (event.key === 'Escape') {
      setIsOpen(false)
    }
  }

  const handleOptionClick = (option) => {
    onChange(option.value)
    setIsOpen(false)
  }

  icon = (
    <IcomoonReact
      iconSet={rebrandIconSet}
      size={16}
      icon={isOpen ? 'Controls-Carrot-Up' : 'Controls-Carrot-Down'}
    />
  )

  return (
    <div
      id={id}
      data-testid={`select-${id}`}
      className={elementStyles.selectContainer}
      ref={selectRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div className={elementStyles.selectBox} onClick={toggleDropdown}>
        <span data-testid={`select-${id}-option-value`} className={elementStyles.selectedValue}>
          {value ? options.find((opt) => opt.value === value)?.name : placeholder}
        </span>
        <span className={elementStyles.icon}>{icon || '^'}</span>
      </div>
      {isOpen && (
        <ul className={elementStyles.dropdown}>
          {options.map((option, index) => (
            <li
              key={option.value}
              data-testid={`select-${id}-option-${index}`}
              className={classNames(elementStyles.option, {
                [elementStyles.highlighted]: highlightedIndex === index
              })}
              onClick={() => handleOptionClick(option)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {option.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

Select.propTypes = {
  id: PropTypes.any,
  value: PropTypes.any,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  icon: PropTypes.node
}

Select.defaultProps = {
  placeholder: 'Select an option',
  icon: '^'
}

export default Select